import { apiFetch } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApiKeySafe {
  id: string;
  organization_id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateApiKeyDto {
  name: string;
  scopes?: string[];
  expires_at?: string;
}

export interface CreateApiKeyResponse {
  apiKey: ApiKeySafe;
  rawKey: string;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const apiKeyService = {
  async list(orgId: string): Promise<ApiKeySafe[]> {
    const res = await apiFetch(`/api/orgs/${orgId}/api-keys`);
    if (!res.ok) throw new Error('Failed to load API keys');
    const body = await res.json();
    return body.data;
  },

  async create(orgId: string, dto: CreateApiKeyDto): Promise<CreateApiKeyResponse> {
    const res = await apiFetch(`/api/orgs/${orgId}/api-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body?.error?.message || 'Failed to create API key');
    }
    return body.data;
  },

  async updateName(orgId: string, keyId: string, name: string): Promise<void> {
    const res = await apiFetch(`/api/orgs/${orgId}/api-keys/${keyId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!res.ok) throw new Error('Failed to update API key');
  },

  async revoke(orgId: string, keyId: string): Promise<void> {
    const res = await apiFetch(`/api/orgs/${orgId}/api-keys/${keyId}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to revoke API key');
  },
};
